import React, { useState, KeyboardEvent } from 'react'
import { MobileColors } from '../../styles/mobileTokens'
import { useNavigate } from 'react-router-dom'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { SearchType } from './hooks/useMobileSearchAPI'
import { MobileSearchTypeDropdown } from './MobileSearchTypeDropdown'
import styled from 'styled-components'

const SearchContainer = styled.div`
  width: 100%;
`

const SEARCH_TYPE_LABELS: Record<SearchType, string> = {
  fb_location: 'First Bite Location',
  fb_company: 'First Bite Company',
  custom_location: 'Custom Location',
  custom_company: 'Custom Company',
}

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background-color: white;
  border: 1px solid ${MobileColors.borderDefault};
  padding: 12px 16px;
  border-radius: 8px;

  &:focus-within {
    border-color: ${MobileColors.backgroundSelected};
    box-shadow: 0px 4px 8px 3px ${MobileColors.shadowBrand};
  }

  &:hover {
    border-color: ${MobileColors.backgroundSelected};
    box-shadow: 0px 4px 8px 3px ${MobileColors.shadowBrand};
  }
`

const SearchIcon = styled.div`
  display: flex;
  align-items: center;
  font-size: 20px;
  color: ${MobileColors.textSecondary};
  flex-shrink: 0;
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: ${MobileColors.textPrimary};
  background: transparent;

  &::placeholder {
    color: ${MobileColors.textMuted};
  }
`

interface MobileSearchBarProps {
  defaultSearchType?: SearchType
  showDropdown?: boolean
  onSubmit?: (query: string, searchType: SearchType) => void
}

export const MobileSearchBar: React.FC<MobileSearchBarProps> = ({
  defaultSearchType = 'fb_company',
  showDropdown = false,
  onSubmit,
}) => {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [searchType, setSearchType] = useState<SearchType>(defaultSearchType)

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(query, searchType)
    } else {
      // Default behavior: navigate to /search with query params
      const params = new URLSearchParams()
      params.set('searchType', searchType)
      if (query.trim()) {
        params.set('q', query.trim())
      }
      navigate(`/search?${params.toString()}`)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSubmit()
  }

  return (
    <SearchContainer>
      {showDropdown && (
        <MobileSearchTypeDropdown
          value={searchType}
          label={SEARCH_TYPE_LABELS[searchType]}
          onChange={setSearchType}
          labelPrefix="Search for a"
        />
      )}
      <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
        <SearchInputWrapper>
          <SearchIcon>
            <PiMagnifyingGlass />
          </SearchIcon>
          <SearchInput
            type="search"
            enterKeyHint="search"
            placeholder={`Search for a ${SEARCH_TYPE_LABELS[searchType]}`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </SearchInputWrapper>
      </form>
    </SearchContainer>
  )
}
